import {
  editorTextBoxLoadsSettled,
  editorTextSizingOf,
  ensureEditorTextBoxStructuralSheet,
  fitRenderedEditorTextBox,
  renderEditorTextBox,
  settleEditorTextBoxFonts,
} from '@/utils/editor-text-box/editorTextBoxAdapter'
import type {
  EditorInkRect,
  EditorResolvedClass,
  EditorTextBoxDocument,
} from '@/utils/editor-text-box/editorTextBoxAdapter'

/**
 * The ink each resolved document paints, by the document itself. A document
 * keeps its identity while its rows, the cascade and the case are unchanged
 * (`editorTextBoxViewModelResults`, `editorTextBoxDraftSelector`), so an
 * unchanged box is never measured twice, a delete measures nothing, and an
 * edit measures the one box it changed. A mounted box remembers the ink its
 * own fit found under loaded faces, so the boxes on the canvas are never
 * rendered a second time offscreen.
 */
const inkByDocument = new WeakMap<EditorTextBoxDocument, EditorInkRect | null>()

export const rememberEditorTextBoxInkRect = (
  document: EditorTextBoxDocument,
  ink: EditorInkRect | null,
): void => {
  inkByDocument.set(document, ink)
}

const renderOffscreen = (
  document: EditorTextBoxDocument,
): { container: HTMLDivElement; resolvedClasses: EditorResolvedClass[] } => {
  const { rect } = document.resolved
  const container = window.document.createElement('div')
  ensureEditorTextBoxStructuralSheet()
  const rendered = renderEditorTextBox(document, {
    rect: { x: 0, y: 0, width: rect.width, height: rect.height },
  })
  container.style.cssText =
    'position:absolute;left:-99999px;top:0;visibility:hidden;pointer-events:none;'
  container.style.width = `${rect.width}px`
  container.style.height = `${rect.height}px`
  container.innerHTML = rendered.html
  window.document.body.appendChild(container)
  return { container, resolvedClasses: rendered.resolvedClasses }
}

/**
 * The ink of a box that is not on the canvas: rendered offscreen, fitted
 * once its faces have landed under the box's own text sizing, and
 * remembered. A fit under a face still loading would measure a fallback, and
 * a fit under another mode would measure text of another size; either would
 * warn about the wrong margin. A reading taken while a load its text draws
 * from may still land (the settle gave up on a face still loading, or a
 * face given up still downloads) is returned but not remembered, so the
 * next check measures again.
 */
export const measureEditorTextBoxInkRect = async (
  document: EditorTextBoxDocument,
): Promise<EditorInkRect | null> => {
  const known = inkByDocument.get(document)
  if (known !== undefined) return known
  let staged: ReturnType<typeof renderOffscreen> | null = null
  try {
    staged = renderOffscreen(document)
    const pending = settleEditorTextBoxFonts(staged.container)
    if (pending) await pending
    const fit = fitRenderedEditorTextBox(staged.container, {
      resolvedClasses: staged.resolvedClasses,
      sizing: editorTextSizingOf(document.box),
    })
    const ink = fit.inkRect ?? null
    if (editorTextBoxLoadsSettled(staged.container))
      inkByDocument.set(document, ink)
    return ink
  } catch {
    return null
  } finally {
    staged?.container.remove()
  }
}
